import { orderBy } from 'lodash';
import { isAfter } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';

import {
  CandidateStatus,
  CandidateStatusPriorityOrder,
  OrderDirection,
  PrimarySkillsRankingOrder,
  SeniorityOrder,
} from '@constants';
import {
  DateTimeRange,
  ICandidateDetails,
  ICandidateInterview,
  IPositionCandidate,
} from '@types';
import { geDateTimeInTimezone } from './date';
import { formatPositionTitle } from './position';

const CanonicalSeniorityNumeral: Record<string, string> = {
  1: 'I',
  2: 'II',
  3: 'III',
  I: 'I',
  II: 'II',
  III: 'III',
};

export const canonicalSeniority = (value: string): string =>
  value
    .trim()
    .split(/\s+/)
    .map((word, index) =>
      index
        ? CanonicalSeniorityNumeral[word.toUpperCase()] ?? word
        : SeniorityOrder.find(
            (known) => known.toLowerCase() === word.toLowerCase(),
          ) ?? word,
    )
    .join(' ');

const LegacyCandidateStatusMap: Record<string, CandidateStatus> = {
  'Call Requested': CandidateStatus.CallRequested,
  'Setting up interview': CandidateStatus.CallRequested,
  'Call Cancelled': CandidateStatus.CallCancelled,
  'Call Scheduled': CandidateStatus.CallScheduled,
  'Call Done': CandidateStatus.InterviewDone,
  'Call Passed': CandidateStatus.CallPassed,
  'Call Failed': CandidateStatus.CallFailed,
  'Approved for Hiring': CandidateStatus.HiringApproved,
};

export const normalizeCandidateStatus = (
  candidateStatus: CandidateStatus | string | null | undefined,
): CandidateStatus | null => {
  if (!candidateStatus) return null;

  if (
    Object.values(CandidateStatus).includes(candidateStatus as CandidateStatus)
  )
    return candidateStatus as CandidateStatus;

  return LegacyCandidateStatusMap[candidateStatus] || null;
};

/**
 * When is a booking still the client's to move?
 *
 * The condition the three interview endpoints actually enforce — a round exists and
 * nobody has recorded its result yet:
 *
 *   if (!isEmpty(latestCI?.result)) throw new ForbiddenException();
 *
 * Status is NOT it. Launchpod imposes no state machine on `interview[]`: a recruiter can
 * write a round's result while leaving the submission at `Call Scheduled`, and it
 * recovers a cancelled round in place rather than abandoning it. Gating on status was
 * wrong in both directions — it blocked a cancelled interview the server would have
 * rebooked, and offered a reschedule on a written-up round that comes back 403.
 * See docs/client-dashboard/handoff-hiring-flow-gaps.md §5a.
 */
export const isInterviewRoundOpen = (
  round: ICandidateInterview | null | undefined,
): boolean => Boolean(round && !round.result);

/**
 * A booked interview whose slot has come and gone with nothing recorded against it.
 *
 * A date in the past is not a status, it is a stall: either the interview happened and
 * nobody wrote it up, or it did not happen at all, and both need the reader to act while
 * a row that just prints last Tuesday asks for nothing. One definition, read by the list
 * cell and by the profile bar, so the two cannot disagree about the urgency of one row.
 */
export const isInterviewOverdue = (
  candidateStatus: CandidateStatus | string | null | undefined,
  scheduledSlot: DateTimeRange[] | null | undefined,
  clientTimezone: string | null | undefined,
): boolean => {
  if (
    normalizeCandidateStatus(candidateStatus) !== CandidateStatus.CallScheduled
  )
    return false;

  const booked = bookedSlotAt(scheduledSlot, clientTimezone);

  return Boolean(booked?.isBefore(dayjs()));
};

/** The booked slot, in the reader's offset. Each surface formats it at its own density. */
export const bookedSlotAt = (
  scheduledSlot: DateTimeRange[] | null | undefined,
  clientTimezone: string | null | undefined,
): Dayjs | null => {
  const start = scheduledSlot?.[0]?.start;

  return start ? geDateTimeInTimezone(start, clientTimezone ?? null) : null;
};

/** Rounds that have actually happened — written up, or booked for a time now past. */
export const interviewRoundsHeld = (
  rounds: ICandidateInterview[] | null | undefined,
): number =>
  (rounds ?? []).filter(
    (round) =>
      round.result ||
      (round.scheduledSlot?.[0]?.end &&
        isAfter(new Date().toISOString(), round.scheduledSlot[0].end!)),
  ).length;

export const sortCandidateGroups = <T>(
  groups: [string, T][],
  groupingOption: keyof IPositionCandidate | 'position' | '',
): [string, T][] => {
  const getGroupOrder = (key: string): number | string => {
    switch (groupingOption) {
      case 'seniority': {
        const index = SeniorityOrder.indexOf(key);
        return index === -1 ? SeniorityOrder.length : index;
      }

      case 'status': {
        const status = normalizeCandidateStatus(key);
        const index = status
          ? CandidateStatusPriorityOrder.indexOf(status)
          : -1;
        return index === -1 ? CandidateStatusPriorityOrder.length : index;
      }

      default:
        return key.toLowerCase();
    }
  };

  return [...groups].sort((a, b) => {
    const orderA = getGroupOrder(a[0]);
    const orderB = getGroupOrder(b[0]);

    if (typeof orderA === 'number' && typeof orderB === 'number')
      return orderA - orderB;

    return String(orderA).localeCompare(String(orderB));
  });
};

const inactiveCandidateStatuses = new Set<CandidateStatus>([
  CandidateStatus.Rejected,
  CandidateStatus.CallFailed,
  CandidateStatus.CandidateNoLongerAvailable,
]);

export const isInactiveCandidate = (candidate: IPositionCandidate): boolean => {
  const status = normalizeCandidateStatus(candidate.status);

  return Boolean(status && inactiveCandidateStatuses.has(status));
};

export type CandidateListSortKey =
  | keyof IPositionCandidate
  | 'technicalRanking'
  | 'softSkills';

export const sortListCandidates = <T extends IPositionCandidate>(
  candidates: T[],
  sortColumn: CandidateListSortKey | null,
  sortDirection: OrderDirection | null,
  { floatStarred = false }: { floatStarred?: boolean } = {},
): T[] => {
  if (!sortColumn || !sortDirection) return candidates;

  const byColumn = (c: T) => {
    switch (sortColumn) {
      case 'firstName':
        return `${c.firstName} ${c.lastName}`.toLowerCase();

      case 'seniority':
        return SeniorityOrder.indexOf(c.seniority || '');

      case 'skills':
        return (c.skills || []).join(', ').toLowerCase();

      case 'status': {
        const status = normalizeCandidateStatus(c.status);
        const index = status
          ? CandidateStatusPriorityOrder.indexOf(status)
          : -1;

        return index === -1 ? CandidateStatusPriorityOrder.length : index;
      }

      case 'technicalRanking':
      case 'softSkills': {
        const tier =
          sortColumn === 'technicalRanking'
            ? c.ranking?.technicalInterview
            : c.ranking?.softSkills;
        const index = tier ? PrimarySkillsRankingOrder.indexOf(tier) : -1;

        // Unrated sinks in both directions, as an unscored test task does.
        if (index === -1)
          return sortDirection === OrderDirection.ASC ? Infinity : -Infinity;
        return index;
      }

      case 'testTask': {
        const score = c.testTask?.score;
        if (typeof score !== 'number')
          return sortDirection === OrderDirection.ASC ? Infinity : -Infinity;
        return score;
      }

      case 'updatedAt':
        return new Date(c.updatedAt || 0).getTime();

      default: {
        const value = (c as any)[sortColumn];
        if (Array.isArray(value)) return value.join(', ').toLowerCase();
        if (typeof value === 'string') return value.toLowerCase();
        return value;
      }
    }
  };

  if (!floatStarred) return orderBy(candidates, byColumn, [sortDirection]);

  return orderBy(
    candidates,
    [
      (c) => (isInactiveCandidate(c) ? 1 : 0),
      (c) => (c.isStarred ? 0 : 1),
      byColumn,
    ],
    [OrderDirection.ASC, OrderDirection.ASC, sortDirection],
  );
};

/**
 * Is this candidate waiting on the reader?
 *
 * The same two states that draw action buttons in the Next steps cell, so the tint and
 * the buttons cannot disagree about whose turn it is.
 */
export const awaitsReader = (candidate: IPositionCandidate): boolean => {
  const status = normalizeCandidateStatus(candidate.status);

  return (
    status === CandidateStatus.Submitted ||
    status === CandidateStatus.InterviewDone
  );
};

export const candidateSubtitle = (
  candidate: Pick<ICandidateDetails, 'profile' | 'positions'>,
  { withCountry = false } = {},
): string =>
  [
    candidate.profile?.seniority,
    withCountry ? candidate.profile?.country : null,
    (candidate.positions ?? []).map(formatPositionTitle).filter(Boolean)[0],
  ]
    .filter(Boolean)
    .join(' · ');
